// # Filename: src/components/home/HomeGameCard.jsx
import React from "react";
import Card from "../ui/Card";
import Badge from "../ui/Badge";

const GAME_META = {
  ttt:            { tagline: "Real-time 1v1 strategy", pattern: "xo" },
  "connect-four": { tagline: "Drop pieces, connect four to win", pattern: "dots" },
  checkers:       { tagline: "Capture pieces and crown kings", pattern: "checkers" },
  poker:          { tagline: "Cash tables, AI hands, and tournament runs", pattern: "poker" },
  sudoku:         { tagline: "Fill the 9x9 grid with logic", pattern: "grid" },
};

function StandardCard({ game, onComingSoon }) {
  const Icon = game.icon;
  const { tagline, pattern } = GAME_META[game.id] ?? { tagline: "", pattern: "grid" };
  const isLive = game.statusText === "Live";

  return (
    <Card
      variant="glass"
      interactive
      className="group relative min-h-[154px] overflow-hidden p-3.5 sm:min-h-[162px] sm:p-4 [@media(min-width:768px)_and_(max-height:700px)]:min-h-[146px] [@media(min-width:768px)_and_(max-height:700px)]:p-3"
    >
      <GamePattern pattern={pattern} />
      <div className="relative flex items-start justify-between mb-3 [@media(min-width:768px)_and_(max-height:700px)]:mb-2.5">
        <div className="grid h-10 w-10 place-items-center rounded-xl border border-brand-cyan/20 bg-slate-950/50 text-brand-cyan shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition group-hover:border-brand-cyan/40 group-hover:bg-brand-cyan/10 sm:h-11 sm:w-11">
          <Icon size={22} className="text-brand-cyan" />
        </div>
        {isLive && <LivePip />}
      </div>

      <h3 className="relative mb-1 text-base font-semibold tracking-tight text-text-primary sm:text-[17px]">
        {game.title}
      </h3>
      <p className="relative mb-3.5 text-[11px] leading-relaxed text-text-secondary sm:text-[12px] [@media(min-width:768px)_and_(max-height:700px)]:mb-3">
        {tagline}
      </p>

      <div className={`relative grid gap-2 sm:flex ${game.actions?.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
        {game.actions ? (
          game.actions.map((action, i) => (
            <ActionButton key={action.id} label={action.label} onClick={action.onClick} primary={i === 0} tone={action.tone} />
          ))
        ) : (
          <ComingSoonButton onClick={() => onComingSoon?.(game.title)} />
        )}
      </div>
    </Card>
  );
}

function FeaturedCard({ game, onComingSoon }) {
  const Icon = game.icon;
  const { tagline } = GAME_META[game.id] ?? { tagline: "" };
  const isLive = game.statusText === "Live";

  return (
    <Card
      variant="glass"
      interactive
      className="group relative overflow-hidden border-brand-cyan/25 bg-[linear-gradient(135deg,rgba(20,184,166,0.16),rgba(255,255,255,0.045)_44%,rgba(255,143,82,0.10))]"
    >
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-cyan/60 to-brand-amber/40" />
      <div className="pointer-events-none absolute -right-16 -top-20 h-72 w-72 rounded-full bg-brand-cyan/10 blur-3xl transition group-hover:bg-brand-cyan/20" />
      <div className="pointer-events-none absolute -bottom-28 left-12 h-72 w-72 rounded-full bg-brand-amber/10 blur-3xl" />
      <div className="pointer-events-none absolute inset-y-0 right-4 hidden w-72 opacity-35 sm:block">
        <PokerTableMotif />
      </div>

      <div className="relative p-4 sm:flex sm:items-center sm:gap-6 sm:p-6 [@media(min-width:768px)_and_(max-height:700px)]:p-4">
        <div className="mb-4 grid h-16 w-16 shrink-0 place-items-center rounded-2xl border border-brand-cyan/30 bg-slate-950/55 text-brand-cyan shadow-[0_18px_50px_rgba(34,211,238,0.12),inset_0_1px_0_rgba(255,255,255,0.08)] sm:mb-0 sm:h-20 sm:w-20">
          <Icon size={42} className="text-brand-cyan" />
        </div>

        <div className="flex-1 min-w-0 mb-4 sm:mb-0">
          <div className="flex flex-wrap items-center gap-3 mb-1.5">
            <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-text-primary">
              {game.title}
            </h3>
            {isLive && <LivePip />}
          </div>
          <p className="max-w-md text-sm leading-relaxed text-text-secondary">{tagline}</p>
          <div className="mt-3 hidden gap-2 sm:flex">
            <MiniStat label="Players" value="2-9" />
            <MiniStat label="Modes" value="3" />
            <MiniStat label="Tables" value="Live" />
          </div>
        </div>

        <div className={`grid gap-2 sm:flex sm:shrink-0 ${game.actions?.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
          {game.actions ? (
            game.actions.map((action, i) => (
              <ActionButton key={action.id} label={action.label} onClick={action.onClick} primary={i === 0} tone={action.tone} />
            ))
          ) : (
            <ComingSoonButton onClick={() => onComingSoon?.(game.title)} />
          )}
        </div>
      </div>
    </Card>
  );
}

export default function HomeGameCard({ game, featured = false, onComingSoon }) {
  return featured
    ? <FeaturedCard game={game} onComingSoon={onComingSoon} />
    : <StandardCard game={game} onComingSoon={onComingSoon} />;
}

function LivePip() {
  return (
    <Badge variant="success" className="relative gap-1.5 overflow-hidden px-2 py-0.5 shadow-[0_0_22px_rgba(52,211,153,0.10)]">
      <span className="absolute inset-0 opacity-0 transition group-hover:opacity-100 bg-gradient-to-r from-transparent via-brand-emerald/10 to-transparent" />
      <span className="relative flex h-2 w-2 items-center justify-center">
        <span className="absolute h-2 w-2 rounded-full bg-brand-emerald/40 animate-ping" />
        <span className="relative h-1.5 w-1.5 rounded-full bg-brand-emerald shadow-glow-emerald" />
      </span>
      <span className="relative">Live</span>
    </Badge>
  );
}

function ActionButton({ label, onClick, primary, tone }) {
  const tournamentClass =
    "bg-brand-amber/90 border-brand-amber/70 text-background-app hover:bg-brand-amber hover:border-brand-amber";
  const primaryClass =
    "bg-brand-cyan/90 border-brand-cyan/70 text-background-app hover:bg-brand-cyan hover:border-brand-cyan";
  const neutralClass =
    "bg-surface border-border-soft text-text-secondary hover:bg-surface-elevated hover:text-text-primary";

  return (
    <button
      type="button"
      onClick={() => onClick()}
      className={`min-h-9 px-2 py-1.5 rounded-button text-[9px] sm:min-h-0 sm:px-3 sm:py-2 sm:text-[11px] font-semibold uppercase tracking-[0.06em] sm:tracking-[0.08em] transition duration-150 border ${
        tone === "tournament" ? tournamentClass : primary ? primaryClass : neutralClass
      }`}
    >
      {label}
    </button>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg border border-border-soft bg-slate-950/35 px-3 py-2">
      <div className="font-mono text-sm font-semibold text-text-primary">
        {value}
      </div>
      <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-text-faint">
        {label}
      </div>
    </div>
  );
}

function GamePattern({ pattern }) {
  const base = "pointer-events-none absolute inset-0 opacity-25 transition group-hover:opacity-45";
  if (pattern === "dots") {
    return <div className={`${base} [background-image:radial-gradient(circle,rgba(34,211,238,.22)_1.5px,transparent_1.5px)] [background-size:28px_28px] [background-position:18px_16px]`} />;
  }
  if (pattern === "checkers") {
    return <div className={`${base} opacity-20 [background-image:linear-gradient(45deg,rgba(34,211,238,.16)_25%,transparent_25%,transparent_75%,rgba(34,211,238,.16)_75%),linear-gradient(45deg,rgba(34,211,238,.16)_25%,transparent_25%,transparent_75%,rgba(34,211,238,.16)_75%)] [background-size:36px_36px] [background-position:0_0,18px_18px]`} />;
  }
  if (pattern === "xo") {
    return <div className={`${base} [background-image:linear-gradient(rgba(34,211,238,.16)_1px,transparent_1px),linear-gradient(90deg,rgba(251,113,133,.13)_1px,transparent_1px)] [background-size:38px_38px]`} />;
  }
  return <div className={`${base} opacity-25 [background-image:linear-gradient(rgba(255,255,255,.12)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.12)_1px,transparent_1px)] [background-size:24px_24px]`} />;
}

function PokerTableMotif() {
  return (
    <div className="relative h-full w-full">
      <div className="absolute right-2 top-1/2 h-36 w-64 -translate-y-1/2 rounded-[999px] border border-brand-cyan/35 bg-brand-cyan/5 shadow-[inset_0_0_44px_rgba(34,211,238,0.08)]" />
      <div className="absolute right-28 top-[42%] h-16 w-11 rotate-[-10deg] rounded-lg border border-white/20 bg-white/90 shadow-card-soft" />
      <div className="absolute right-20 top-[39%] h-16 w-11 rotate-[8deg] rounded-lg border border-white/20 bg-white/85 shadow-card-soft" />
      <div className="absolute right-[128px] top-[47%] h-2.5 w-2.5 rounded-full bg-brand-rose" />
      <div className="absolute right-[92px] top-[45%] h-2.5 w-2.5 rounded-full bg-slate-950" />
      <div className="absolute right-10 top-[55%] h-8 w-8 rounded-full border border-brand-amber/50 bg-brand-amber/20" />
    </div>
  );
}

function ComingSoonButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-4 py-2 rounded-button text-[11px] font-semibold uppercase tracking-wider bg-surface border border-border-soft text-text-faint cursor-default"
    >
      Coming soon
    </button>
  );
}
